import { getApolloContext, useQuery } from "@apollo/client"
import { useSelector } from "react-redux"
import { useContext, useEffect } from "react"

import { GET_BUSINESS_SETTINGS } from "Shared/graphql/queries/getBusinessSettings"
import { PRIMARY_FALLBACK, AI_COLOR_FALLBACK } from "Shared/tokens"
import {
  loadCachedBusinessColors,
  saveCachedBusinessColors,
} from "Shared/utils/businessColorsCache"

export interface IBusinessColors {
  primaryColor: string
  secondaryColor: string
  bannerColor?: string
  separatorColor?: string
  aiColor: string
  alternativeColor: string
  illustrationColor?: string
  sideMenuColor?: string
  headerColor?: string
}

export const useBusinessColors = (): IBusinessColors => {
  const accessToken = useSelector((state: any) => state?.login?.jwt)

  const { data } = useQuery(GET_BUSINESS_SETTINGS, {
    fetchPolicy: "cache-first",
    skip: !accessToken,
  })

  const settings = data?.getBusinessSettings
  // Fallback while Apollo fetches: serve last-known colors from localStorage
  // (instead of brand-agnostic PRIMARY_FALLBACK) to avoid a flash on reload /
  // post-login. Cache is updated below as soon as fresh data arrives.
  const cached = settings ? null : loadCachedBusinessColors()

  // Persist fresh colors so the next mount can serve them synchronously.
  useEffect(() => {
    if (!settings) return
    saveCachedBusinessColors({
      primaryColor: settings.primaryColor || undefined,
      secondaryColor: settings.secondaryColor || undefined,
      bannerColor: settings.bannerColor || undefined,
      separatorColor: settings.separatorColor || undefined,
      aiColor: settings.aiColor || undefined,
      alternativeColor: settings.alternativeActionColor || undefined,
      illustrationColor: settings.illustrationColor || undefined,
      sideMenuColor: settings.sideMenuColor || undefined,
      headerColor: settings.headerColor || undefined,
    })
  }, [settings])

  return {
    primaryColor: settings?.primaryColor || cached?.primaryColor || PRIMARY_FALLBACK,
    secondaryColor: settings?.secondaryColor || cached?.secondaryColor || PRIMARY_FALLBACK,
    bannerColor: settings?.bannerColor || cached?.bannerColor || undefined,
    separatorColor: settings?.separatorColor || cached?.separatorColor || undefined,
    aiColor: settings?.aiColor || cached?.aiColor || AI_COLOR_FALLBACK,
    alternativeColor:
      settings?.alternativeActionColor || cached?.alternativeColor || PRIMARY_FALLBACK,
    illustrationColor: settings?.illustrationColor || cached?.illustrationColor || undefined,
    sideMenuColor: settings?.sideMenuColor || cached?.sideMenuColor || undefined,
    headerColor: settings?.headerColor || cached?.headerColor || undefined,
  }
}

export interface IBusinessFormStyle {
  /** "sharp" (4px) | "default" (8px) | "rounded" (12px) */
  radius?: string
  /** "comfortable" | "compact" */
  density?: string
  /** hex — field background color (default white) */
  fieldBg?: string
  /** true = read-only fields rendered boxed like edit mode */
  readBoxed?: boolean
  /** max height (px) for textareas / rich editors */
  textMaxHeight?: number
  /** number of visible rows for tables/lists */
  tableRows?: number
  /** hex — background of the form section cards (« Group ») */
  groupBg?: string
  /** "sharp" (4px) | "default" (12px) | "rounded" (20px) */
  groupRadius?: string
  /** "none" | "soft" | "strong" */
  groupShadow?: string
}

/** Aucun réglage tenant lisible → les défauts du design-system s'appliquent. */
const NO_FORM_STYLE: IBusinessFormStyle = {}

/** Lecture effective des réglages — suppose un `ApolloProvider` dans l'arbre. */
const useFormStyleFromApollo = (): IBusinessFormStyle => {
  const accessToken = useSelector((state: any) => state?.login?.jwt)
  const { data } = useQuery(GET_BUSINESS_SETTINGS, {
    fetchPolicy: "cache-first",
    skip: !accessToken,
  })
  const fs = data?.getBusinessSettings?.formStyle
  return {
    radius: fs?.radius || undefined,
    density: fs?.density || undefined,
    fieldBg: fs?.fieldBg || undefined,
    readBoxed: fs?.readBoxed || undefined,
    textMaxHeight: fs?.textMaxHeight || undefined,
    tableRows: fs?.tableRows || undefined,
    groupBg: fs?.groupBg || undefined,
    groupRadius: fs?.groupRadius || undefined,
    groupShadow: fs?.groupShadow || undefined,
  }
}

/**
 * Tenant form-appearance knobs (corner radius, density, fond, lecture encadrée,
 * hauteurs) configurés sur les pages admin « Style » et « Branding ». Consommés par
 * `useBusinessTheme` (variables `--field-*`) et par `useJsonFormsContext`.
 * Objet vide si non réglé → défauts du design-system.
 *
 * ⚠️ `useJsonFormsContext` appelle ce hook depuis **chaque** renderer JsonForms.
 * Sans le garde-fou ci-dessous, monter un formulaire hors `ApolloProvider` fait
 * tomber tout le rendu sur l'invariant Apollo « could not find client in the
 * context » — c'est ce qui cassait `QuestionnaireForm.overviewInjection.test`.
 * Les réglages tenant sont un enrichissement : leur indisponibilité doit dégrader
 * vers les défauts, jamais casser le formulaire.
 */
export const useBusinessFormStyle = (): IBusinessFormStyle => {
  const { client } = useContext(getApolloContext())
  // La présence d'un ApolloProvider au-dessus d'un composant est stable pour toute
  // sa durée de vie : l'ordre des hooks ne varie donc pas d'un rendu à l'autre.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return client ? useFormStyleFromApollo() : NO_FORM_STYLE
}

export default useBusinessColors
